import React from 'react';
import PropTypes from 'prop-types';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import RadarChart from './RadarChart';
import {
    getMetricSourcesForRequests,
    hasMetricSources,
    allMetricSourcesAuthorized,
    areGroupBysDifferent,
    getUniqueGroupsFromMetricSeries,
    getAllMetricSeriesFromMetricSources,
    getAllMetricsFromMetricRequests,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import { formatRadarMetricSeries } from '../../utils/radarDataFormatUtils';

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics }) {
    if (metricRequests.length === 0 || metricRequests[0].groupBy.length !== 1) {
        return { errorKey: 'invalidMetricRequests' };
    }

    if (areGroupBysDifferent(metricRequests)) {
        return { errorKey: 'metricRequestGroupBysDifferent' };
    }

    const metricSources = getMetricSourcesForRequests(metrics, metricRequests);
    if (metricSources.some((metricSource) => !metricSource)) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized(metricSources, false)) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources(metricSources, false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        metricSources,
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSources, metricRequests };
}
/* eslint-enable complexity */

export default function RadarChartWrapper(props) {
    const { metricSources, metricRequests, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const allMetricSeries = getAllMetricSeriesFromMetricSources(metricSources);
    const metrics = getAllMetricsFromMetricRequests(metricRequests);
    const groupLabels = getUniqueGroupsFromMetricSeries(
        allMetricSeries,
        metricRequests[0].groupBy[0]
    );

    const formattedData = formatRadarMetricSeries(
        allMetricSeries,
        metrics,
        metricRequests[0].groupBy[0]
    );

    return (
        <RadarChart
            {...props}
            chartProperties={props.configuration}
            dataPoints={formattedData}
            groupLabels={groupLabels}
        />
    );
}

RadarChartWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
};
